import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAuth } from '@tyro/api';
import { MotionContainer, Page, varBounce } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import * as m from 'motion/react-m';
import { type ReactNode, useEffect } from 'react';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router';
import {
  ForbiddenIllustration,
  PageNotFoundIllustration,
  SeverErrorIllustration,
  UnknownErrorIllustration,
} from './illustrations';

const errorIllustration = {
  '404': PageNotFoundIllustration,
  '403': ForbiddenIllustration,
  '500': SeverErrorIllustration,
  '503': SeverErrorIllustration,
  '401': UnknownErrorIllustration,
  unknown: UnknownErrorIllustration,
};

function useErrorStatus(error: unknown): keyof typeof errorIllustration {
  const knownErrorKeys = Object.keys(errorIllustration);
  const typedError = isRouteErrorResponse(error)
    ? { response: error }
    : (error as { response: Response });
  const status = typedError.response?.status;

  return status && knownErrorKeys.includes(String(status))
    ? (String(status) as keyof typeof errorIllustration)
    : 'unknown';
}

interface ErrorElementProps {
  Shell: React.FC<{ children: ReactNode }>;
}

export function ErrorElement({ Shell }: ErrorElementProps) {
  const error = useRouteError();
  const { t } = useTranslation(['common', 'navigation']);
  const status = useErrorStatus(error);
  const Illustration = errorIllustration[status];
  const { logout } = useAuth();

  useEffect(() => {
    if (status === '401') {
      logout();
    } else if (
      (error as { errorCode?: string })?.errorCode === 'authority_mismatch'
    ) {
      localStorage.clear();
      window.location.replace('/');
    } else {
      console.error(error);
    }
  }, [status, error]);

  const errorPage = (
    <Page title={t(`navigation:errors.${status}.title`)}>
      <Container maxWidth="xl">
        <MotionContainer
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <m.div variants={varBounce().in}>
            <Typography variant="h3" component="p">
              {t(`navigation:errors.${status}.title`)}
            </Typography>
          </m.div>

          <m.div variants={varBounce().in}>
            <Typography
              sx={{
                color: 'text.secondary',
                maxWidth: 'sm',
                textAlign: 'center',
              }}
            >
              {t(`navigation:errors.${status}.description`)}
            </Typography>
          </m.div>

          <m.div variants={varBounce().in}>
            <Illustration
              sx={{
                height: 260,
                my: { xs: 5, sm: 10 },
              }}
            />
          </m.div>

          <Stack spacing={2}>
            <Button to="/" component={Link} size="large" variant="contained">
              {t('navigation:errors.action')}
            </Button>
            <Button onClick={logout} size="large" variant="text">
              {t('common:logout')}
            </Button>
          </Stack>
        </MotionContainer>
      </Container>
    </Page>
  );

  if (['500', '503', 'unknown'].includes(status)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        {errorPage}
      </Box>
    );
  }

  return <Shell>{errorPage}</Shell>;
}
